/**
 * Voice AI — IVR Schedule Tab
 * Time-of-day routing schedules per menu.
 * Self-loading: fetches own data when menu changes.
 * Pattern: agent-overview-tab.js (self-loading)
 */

import { LitElement, html } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { ivrStyles } from '../../styles/ivr-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { ivrService } from '../../services/ivr-service.js';
import { confirmService } from '../../services/confirm-service.js';
import '../voice-modal.js';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };
const DAY_FULL = { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };
const SCHEDULE_TYPES = [
  { value: 'business_hours', label: 'Business Hours', icon: 'fa-clock' },
  { value: 'after_hours', label: 'After Hours', icon: 'fa-moon' },
  { value: 'holiday', label: 'Holiday', icon: 'fa-calendar-star' },
  { value: 'custom', label: 'Custom', icon: 'fa-sliders' },
];

export class IvrScheduleTab extends LitElement {
  static properties = {
    menu: { type: Object },
    _schedules: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _showModal: { type: Boolean, state: true },
    _editing: { type: Object, state: true },
    _dayStates: { type: Object, state: true },
  };

  static styles = [designSystem, componentStyles, ivrStyles];

  constructor() {
    super();
    this.menu = null;
    this._schedules = [];
    this._loading = true;
    this._showModal = false;
    this._editing = null;
    this._lastMenuId = null;
    this._dayStates = {};
  }

  updated(changed) {
    if (changed.has('menu') && this.menu?.id !== this._lastMenuId) {
      this._lastMenuId = this.menu?.id;
      this._loadSchedules();
    }
  }

  async _loadSchedules() {
    if (!this.menu) return;
    this._loading = true;
    try {
      const data = await ivrService.listSchedules(this.menu.id);
      if (data.success) {
        this._schedules = data.schedules || [];
      }
    } catch (err) {
      console.error('[IvrScheduleTab] Load failed:', err);
    } finally {
      this._loading = false;
    }
  }

  _toast(message) {
    this.dispatchEvent(new CustomEvent('toast', { detail: { message } }));
  }

  _openAdd() {
    const defaultHours = {};
    DAYS.forEach(d => {
      defaultHours[d] = { enabled: d !== 'saturday' && d !== 'sunday', start: '08:00', end: '17:00' };
    });
    this._editing = {
      menu_id: this.menu.id,
      name: '',
      schedule_type: 'business_hours',
      business_hours: defaultHours,
      timezone: 'America/New_York',
      priority: 0,
      holiday_dates: [],
      override_action: 'use_menu',
      override_message_tts: '',
      is_active: 1,
    };
    this._dayStates = {};
    DAYS.forEach(d => { this._dayStates[d] = defaultHours[d].enabled; });
    this._showModal = true;
  }

  _openEdit(schedule) {
    this._editing = {
      ...schedule,
      business_hours: schedule.business_hours || {},
      holiday_dates: schedule.holiday_dates || [],
    };
    this._dayStates = {};
    DAYS.forEach(d => {
      const dh = this._editing.business_hours[d];
      this._dayStates[d] = dh ? dh.enabled !== false : true;
    });
    this._showModal = true;
  }

  _closeModal() {
    this._showModal = false;
    this._editing = null;
  }

  _toggleDay(day) {
    this._dayStates = { ...this._dayStates, [day]: !this._dayStates[day] };
  }

  async _saveSchedule() {
    const s = this._editing;
    const root = this.shadowRoot;
    s.name = root.querySelector('#sched-name')?.value || 'Default Schedule';
    s.schedule_type = root.querySelector('#sched-type')?.value || 'business_hours';
    s.timezone = root.querySelector('#sched-tz')?.value || 'America/New_York';
    s.priority = parseInt(root.querySelector('#sched-priority')?.value) || 0;
    s.override_action = root.querySelector('#sched-override')?.value || 'use_menu';
    s.override_message_tts = root.querySelector('#sched-override-msg')?.value || '';
    s.is_active = this._editing.is_active;

    // Read holiday dates
    const holidayInput = root.querySelector('#sched-holidays')?.value || '';
    s.holiday_dates = holidayInput.split(',').map(d => d.trim()).filter(Boolean);

    // Read business hours from toggle states + time inputs
    const hours = {};
    DAYS.forEach(day => {
      const enabled = this._dayStates[day] ?? true;
      const start = root.querySelector(`#start-${day}`)?.value || '08:00';
      const end = root.querySelector(`#end-${day}`)?.value || '17:00';
      hours[day] = { enabled, start, end };
    });
    s.business_hours = hours;

    try {
      let data;
      if (s.id) {
        data = await ivrService.updateSchedule(s.id, s);
      } else {
        data = await ivrService.createSchedule(s);
      }
      if (data.success) {
        this._closeModal();
        this._loadSchedules();
        this._toast(s.id ? 'Schedule updated' : 'Schedule created');
      } else {
        this._toast('Save failed: ' + (data.error || 'Unknown'));
      }
    } catch (err) {
      this._toast('Save failed: ' + err.message);
    }
  }

  async _confirmDelete(id) {
    const confirmed = await confirmService.show({
      title: 'Delete Schedule',
      icon: 'fa-triangle-exclamation',
      description: 'This action cannot be undone.',
    });
    if (!confirmed) return;
    try {
      const data = await ivrService.deleteSchedule(id);
      if (data.success) {
        this._loadSchedules();
        this._toast('Schedule deleted');
      }
    } catch (err) {
      this._toast('Delete failed: ' + err.message);
    }
  }

  _getTypeIcon(type) {
    const t = SCHEDULE_TYPES.find(s => s.value === type);
    return t?.icon || 'fa-clock';
  }

  render() {
    return html`
      ${iconStyles}

      <div class="section">
        <div class="section-header">
          <div>
            <div class="section-title"><i class="fa-sharp fa-light fa-calendar-clock"></i> Schedules</div>
            <div class="section-description">Control when and how the IVR menu is active. Higher priority schedules are checked first.</div>
          </div>
          <button class="btn btn-primary btn-sm" @click=${this._openAdd}>
            <i class="fa-sharp fa-light fa-plus"></i> Add Schedule
          </button>
        </div>

        ${this._loading ? html`
          ${[1, 2].map(() => html`
            <div class="option-row">
              <div class="skeleton-line" style="width:36px;height:36px;border-radius:var(--radius-md);"></div>
              <div class="option-info">
                <div class="skeleton-line" style="width:60%;height:14px;margin-bottom:6px;"></div>
                <div class="skeleton-line xs" style="width:40%;"></div>
              </div>
            </div>
          `)}
        ` : this._schedules.length === 0 ? html`
          <div class="empty-state">
            <div class="empty-state-icon"><i class="fa-sharp fa-light fa-calendar-clock"></i></div>
            <div class="empty-state-title">No schedules</div>
            <div class="empty-state-description">The IVR menu will be active at all times. Add schedules for business hours, after-hours, or holiday routing.</div>
          </div>
        ` : html`
          ${this._schedules.map(s => html`
            <div class="option-row">
              <div class="option-digit">
                <i class="fa-sharp fa-light ${this._getTypeIcon(s.schedule_type)}" style="font-size:14px;"></i>
              </div>
              <div class="option-info">
                <div class="option-label">${s.name || 'Unnamed'}</div>
                <div class="option-action">
                  <span class="badge ${s.is_active ? 'badge-success' : 'badge-neutral'}">
                    <span class="status-dot ${s.is_active ? 'active' : 'inactive'}"></span>
                    ${s.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span>${(s.schedule_type || '').replace(/_/g, ' ')}</span>
                  <span style="color:var(--color-text-tertiary);">P${s.priority}</span>
                  ${s.override_action !== 'use_menu' ? html`
                    <span class="badge badge-warning">${(s.override_action || '').replace(/_/g, ' ')}</span>
                  ` : ''}
                </div>
              </div>
              <div class="option-actions">
                <button title="Edit" @click=${() => this._openEdit(s)}>
                  <i class="fa-sharp fa-light fa-pen-to-square"></i>
                </button>
                <button class="danger" title="Delete" @click=${() => this._confirmDelete(s.id)}>
                  <i class="fa-sharp fa-light fa-trash"></i>
                </button>
              </div>
            </div>
          `)}
        `}
      </div>

      <!-- Schedule Modal -->
      ${this._showModal ? this._renderModal() : ''}

    `;
  }

  _renderModal() {
    const s = this._editing;
    const isEdit = !!s?.id;
    const hours = s?.business_hours || {};

    return html`
      <voice-modal ?open=${this._showModal} title="${isEdit ? 'Edit Schedule' : 'Add Schedule'}" width="640px"
        @modal-close=${this._closeModal}>
        <div style="padding:var(--space-6, 24px);">
        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Schedule Name</label>
            <input type="text" class="form-input" id="sched-name" .value=${s?.name || ''} placeholder="e.g. Business Hours">
          </div>
          <div class="form-group">
            <label class="form-label">Type</label>
            <select class="form-select" id="sched-type">
              ${SCHEDULE_TYPES.map(t => html`
                <option value="${t.value}" ?selected=${s?.schedule_type === t.value}>${t.label}</option>
              `)}
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Timezone</label>
            <input type="text" class="form-input" id="sched-tz" .value=${s?.timezone || 'America/New_York'}>
          </div>
          <div class="form-group">
            <label class="form-label">Priority</label>
            <input type="number" class="form-input" id="sched-priority" min="0" max="100" .value=${String(s?.priority || 0)}>
            <div class="form-hint">Higher = checked first</div>
          </div>
        </div>

        <!-- Business Hours Grid -->
        <div class="section" style="margin-top:var(--space-4);padding:var(--space-4);">
          <div class="section-title" style="margin-bottom:var(--space-3);font-size:var(--text-sm);">
            <i class="fa-sharp fa-light fa-clock"></i> Business Hours
          </div>
          ${DAYS.map(day => {
            const dh = hours[day] || { enabled: true, start: '08:00', end: '17:00' };
            const enabled = this._dayStates[day] ?? true;
            return html`
              <div class="schedule-day-row ${enabled ? '' : 'disabled'}">
                <div class="toggle ${enabled ? 'active' : ''}"
                  @click=${() => this._toggleDay(day)}></div>
                <span class="schedule-day-label">${DAY_FULL[day]}</span>
                <div class="schedule-times">
                  <input type="time" id="start-${day}" .value=${dh.start || '08:00'}>
                  <span>to</span>
                  <input type="time" id="end-${day}" .value=${dh.end || '17:00'}>
                </div>
              </div>
            `;
          })}
        </div>

        <!-- Holiday Dates -->
        <div class="form-group" style="margin-top:var(--space-4);">
          <label class="form-label">Holiday Dates</label>
          <input type="text" class="form-input" id="sched-holidays"
            .value=${(s?.holiday_dates || []).join(', ')}
            placeholder="2026-12-25, 2026-01-01">
          <div class="form-hint">Comma-separated YYYY-MM-DD format</div>
        </div>

        <!-- Override Action -->
        <div class="form-grid" style="margin-top:var(--space-4);">
          <div class="form-group">
            <label class="form-label">Override Action</label>
            <select class="form-select" id="sched-override">
              <option value="use_menu" ?selected=${s?.override_action === 'use_menu'}>Use Menu (no override)</option>
              <option value="route_to_agent" ?selected=${s?.override_action === 'route_to_agent'}>Route to Agent</option>
              <option value="play_message" ?selected=${s?.override_action === 'play_message'}>Play Message</option>
              <option value="voicemail" ?selected=${s?.override_action === 'voicemail'}>Voicemail</option>
              <option value="hangup" ?selected=${s?.override_action === 'hangup'}>Hang Up</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Active</label>
            <div class="toggle-row">
              <div class="toggle ${s?.is_active !== 0 ? 'active' : ''}"
                @click=${() => { this._editing = { ...this._editing, is_active: this._editing.is_active ? 0 : 1 }; }}></div>
              <span class="toggle-label">Schedule is active</span>
            </div>
          </div>
          <div class="form-group full">
            <label class="form-label">Override Message (TTS)</label>
            <textarea class="form-textarea" id="sched-override-msg" rows="2"
              placeholder="e.g. Our office is currently closed...">${s?.override_message_tts || ''}</textarea>
          </div>
        </div>
        </div>

        <div slot="footer">
          <button class="btn btn-secondary" @click=${this._closeModal}>Cancel</button>
          <button class="btn btn-primary" @click=${this._saveSchedule}>
            ${isEdit ? 'Update' : 'Create'} Schedule
          </button>
        </div>
      </voice-modal>
    `;
  }
}

customElements.define('ivr-schedule-tab', IvrScheduleTab);
